import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Modal, Space, message } from 'antd';
import dayjs from 'dayjs';
import { getInformeAnualMantencionesApi } from 'api/informeMantenciones';
import InformeAnualDowload from 'PDF/InformeAnualMantenciones/Dowload';

const { RangePicker } = DatePicker;

const defaultRange = () => [dayjs().subtract(1, 'year'), dayjs()];

function InformeAnualMantencionesModal({ open, onClose, clienteId, clienteNombre }) {
  const [range, setRange] = useState(defaultRange);
  const [loading, setLoading] = useState(false);
  const [informe, setInforme] = useState(null);

  useEffect(() => {
    if (!open) return;
    setRange(defaultRange());
    setInforme(null);
  }, [open]);

  // Límite defensivo: rango máximo 2 años para evitar queries gigantes.
  const rangeTooLong = useMemo(() => {
    if (!range?.[0] || !range?.[1]) return false;
    return range[1].diff(range[0], 'day') > 730;
  }, [range]);

  const disabledDate = (current) => {
    // No permitir fechas futuras (un informe "hasta ayer o antes").
    if (!current) return false;
    return current.isAfter(dayjs().endOf('day'));
  };

  const handleGenerar = async () => {
    if (!range?.[0] || !range?.[1]) {
      message.warning('Selecciona un rango de fechas');
      return;
    }
    if (rangeTooLong) {
      message.warning('El rango máximo es de 2 años');
      return;
    }
    setLoading(true);
    try {
      const desde = range[0].format('YYYY-MM-DD');
      const hasta = range[1].format('YYYY-MM-DD');
      const res = await getInformeAnualMantencionesApi(clienteId, desde, hasta);
      setInforme(res.data);
    } catch (err) {
      message.error(err?.response?.data?.message || 'Error al generar el informe anual');
    } finally {
      setLoading(false);
    }
  };

  // Aplanar payload del backend al shape que el TemplateEngine consume:
  // cliente/periodo extendidos al top-level + closed_at ya formateado como
  // string (evita issues de timezone al renderizar en @react-pdf).
  const flatData = useMemo(() => {
    if (!informe) return null;
    return {
      cliente_nombre: informe.cliente?.nombre ?? '',
      cliente_rut: informe.cliente?.rut ?? '',
      periodo_desde: informe.periodo?.desde ?? '',
      periodo_hasta: informe.periodo?.hasta ?? '',
      total_mantenciones: informe.mantenciones?.length ?? 0,
      mantenciones: (informe.mantenciones ?? []).map((m) => ({
        ...m,
        closed_at_fmt: m.closed_at ? dayjs(m.closed_at).format('YYYY-MM-DD') : '',
        observaciones: m.observaciones ?? '',
      })),
    };
  }, [informe]);

  const fileName = useMemo(() => {
    if (!informe) return 'informe-anual-mantenciones.pdf';
    const base = (clienteNombre || informe.cliente?.nombre || 'cliente')
      .toLowerCase()
      .replace(/\s+/g, '-');
    return `informe-anual-${base}-${informe.periodo?.desde}-${informe.periodo?.hasta}.pdf`;
  }, [informe, clienteNombre]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Generar informe anual de mantenciones"
      footer={null}
      width={560}
      destroyOnHidden
    >
      {!informe ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <p style={{ margin: 0 }}>
            Selecciona el rango de fechas (por defecto los últimos 12 meses) para generar el PDF con
            las mantenciones cerradas en ese período.
          </p>
          <RangePicker
            value={range}
            onChange={setRange}
            disabledDate={disabledDate}
            style={{ width: '100%' }}
            allowClear={false}
          />
          {rangeTooLong && (
            <Alert
              type="warning"
              showIcon
              message="El rango seleccionado supera los 2 años. Achicá la ventana."
            />
          )}
          <Button
            type="primary"
            onClick={handleGenerar}
            disabled={!range?.[0] || !range?.[1] || rangeTooLong}
            loading={loading}
            block
          >
            Generar informe
          </Button>
        </Space>
      ) : (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          {flatData?.total_mantenciones === 0 && (
            <Alert
              type="info"
              showIcon
              message="No hay mantenciones cerradas en el período seleccionado"
              description="El PDF se genera igual con la tabla vacía."
              style={{ marginBottom: 16, textAlign: 'left' }}
            />
          )}
          <InformeAnualDowload data={flatData} config={{}} fileName={fileName} />
          <div style={{ marginTop: 16 }}>
            <Button onClick={() => setInforme(null)}>Elegir otro rango</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default InformeAnualMantencionesModal;
